import {
    AuthenticationResult,
    PopupRequest,
    PublicClientApplication,
    RedirectRequest,
    SilentRequest
} from '@azure/msal-browser';
import { VueConstructor } from 'vue';
import { loginRequest } from '../config/authConfig';

declare module 'vue/types/vue' {
    interface Vue {
        $msal: MSALPlugin;
        $adal: any; // For backward compatibility
    }
}

export interface IMSALPlugin {
    loginPopup(request?: PopupRequest): Promise<AuthenticationResult>;
    loginRedirect(request?: RedirectRequest): Promise<void>;
    logoutPopup(): Promise<void>;
    logoutRedirect(): Promise<void>;
    acquireTokenSilent(request: SilentRequest): Promise<AuthenticationResult>;
    getAllAccounts(): any[];
    getActiveAccount(): any;
}

class MSALPlugin implements IMSALPlugin {
    instance: PublicClientApplication;

    constructor(msalInstance: PublicClientApplication) {
        this.instance = msalInstance;
    }

    async loginPopup(request?: PopupRequest): Promise<AuthenticationResult> {
        const popupRequest = request || {
            scopes: ['openid', 'profile']
        };
        return await this.instance.loginPopup(popupRequest);
    }

    async loginRedirect(request?: RedirectRequest): Promise<void> {
        const redirectRequest = request || {
            scopes: ['openid', 'profile']
        };
        return await this.instance.loginRedirect(redirectRequest);
    }

    async logoutPopup(): Promise<void> {
        return await this.instance.logoutPopup();
    }

    async logoutRedirect(): Promise<void> {
        return await this.instance.logoutRedirect();
    }

    async acquireTokenSilent(request: SilentRequest): Promise<AuthenticationResult> {
        return await this.instance.acquireTokenSilent(request);
    }

    getAllAccounts(): any[] {
        return this.instance.getAllAccounts();
    }

    getActiveAccount(): any {
        return this.instance.getActiveAccount();
    }

    setActiveAccount(account: any): void {
        this.instance.setActiveAccount(account);
    }

    async handleRedirectPromise(): Promise<AuthenticationResult | null> {
        return await this.instance.handleRedirectPromise();
    }

    isAuthenticated(): boolean {
        return this.getAllAccounts().length > 0;
    }

    getAccount(): any {
        const accounts = this.getAllAccounts();
        return accounts.length > 0 ? accounts[0] : null;
    }

    async logout(): Promise<void> {
        return await this.logoutRedirect();
    }

    async login(): Promise<void> {
        return await this.loginRedirect(loginRequest);
    }
}

const MSALVuePlugin = {
    install(Vue: VueConstructor, msalInstance: PublicClientApplication) {
        const msalPlugin = new MSALPlugin(msalInstance);

        Vue.prototype.$msal = msalPlugin;

        // For backward compatibility with vue-adal, create an $adal alias
        Vue.prototype.$adal = {
            login: () => msalPlugin.loginRedirect(loginRequest),
            logout: () => msalPlugin.logoutRedirect(),
            isAuthenticated: () => msalPlugin.isAuthenticated(),
            getUser: () => msalPlugin.getAccount(),
            getCachedToken: (resource: string): string | null => {
                // This would need to be implemented based on your specific needs
                // getCachedToken is deprecated. Use acquireTokenSilent instead.
                return null;
            }
        };
    }
};

export default MSALVuePlugin;
